import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CalendarBlank, Plus, PencilSimple, Trash, ArrowsClockwise,
  CheckCircle, XCircle, WarningCircle, Info,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { invalidateHolidayCache } from '@/hooks/useHolidays';

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const YEARS = [2025, 2026, 2027, 2028];

const WEEKDAY_LABEL = ['月', '火', '水', '木', '金', '土', '日'];
const KIND_STYLE = {
  official: 'bg-rose-100 text-rose-700 border-rose-200',
  company: 'bg-orange-100 text-orange-700 border-orange-200',
};

const emptyForm = () => ({ date: '', name: '', note: '' });

export default function HolidaysSettings() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [view, setView] = useState({ items: [], weekend: { saturday_off: true, sunday_off: true } });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [overrideBusy, setOverrideBusy] = useState(new Set()); // date-strings currently in-flight
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // company holiday being edited (or null for new)
  const [form, setForm] = useState(emptyForm());
  const [verifyReport, setVerifyReport] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    invalidateHolidayCache(year);
    axios.get(`${API_ROOT}/holidays`, { params: { year } })
      .then((r) => setView(r.data))
      .catch(() => toast.error('祝日データの取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const updateWeekend = async (key, val) => {
    const next = { ...view.weekend, [key]: val };
    try {
      await axios.put(`${API_ROOT}/holidays/settings`, next);
      setView((prev) => ({ ...prev, weekend: next }));
      toast.success('曜日設定を更新しました');
      invalidateHolidayCache();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    }
  };

  const openNewCompany = () => {
    setEditing(null);
    setForm({ ...emptyForm(), date: `${year}-01-01` });
    setDialogOpen(true);
  };
  const openEditCompany = (item) => {
    setEditing(item);
    setForm({ date: item.date, name: item.name, note: item.note || '' });
    setDialogOpen(true);
  };

  const saveCompany = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) {
      toast.error('YYYY-MM-DD 形式で入力してください');
      return;
    }
    if (!form.name.trim()) {
      toast.error('名称を入力してください');
      return;
    }
    setBusy(true);
    try {
      const body = { date: form.date, name: form.name.trim(), note: form.note.trim() || null };
      if (editing) {
        await axios.put(`${API_ROOT}/holidays/company/${editing.id || editing._id}`, body);
        toast.success('会社休業日を更新しました');
      } else {
        // Need id — server returns full doc
        await axios.post(`${API_ROOT}/holidays/company`, body);
        toast.success('会社休業日を追加しました');
      }
      setDialogOpen(false);
      invalidateHolidayCache();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setBusy(false); }
  };

  const deleteCompany = async (item) => {
    if (!window.confirm(`${item.date} ${item.name} を削除しますか？`)) return;
    try {
      await axios.delete(`${API_ROOT}/holidays/company/${item.id || item._id}`);
      toast.success('削除しました');
      invalidateHolidayCache();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除に失敗しました');
    }
  };

  const toggleOverride = async (item) => {
    // Guard: skip if this date is already being toggled
    if (overrideBusy.has(item.date)) return;
    setOverrideBusy((prev) => new Set(prev).add(item.date));
    try {
      if (item.overridden) {
        await axios.delete(`${API_ROOT}/holidays/override/${item.date}`);
        toast.success(`${item.name} の上書きを解除しました`);
      } else {
        await axios.post(`${API_ROOT}/holidays/override`, { date: item.date, disabled: true, note: '' });
        toast.success(`${item.name} を平日として扱う設定にしました`);
      }
      invalidateHolidayCache();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally {
      setOverrideBusy((prev) => {
        const next = new Set(prev);
        next.delete(item.date);
        return next;
      });
    }
  };

  const verify = async () => {
    setBusy(true);
    setVerifyReport(null);
    try {
      const r = await axios.post(`${API_ROOT}/holidays/verify`, null, { params: { year } });
      setVerifyReport(r.data);
      if (r.data.in_sync) {
        toast.success('外部データと完全一致しています');
      } else {
        toast.info('外部データとの差分があります（下部レポート参照）');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || '検証に失敗しました');
    } finally { setBusy(false); }
  };

  const companyItems = view.items.filter((it) => it.kind === 'company');
  const officialItems = view.items.filter((it) => it.kind === 'official');
  const overriddenCount = officialItems.filter((it) => it.overridden).length;

  return (
    <div className="space-y-5 page-enter" data-testid="holidays-settings">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarBlank size={22} weight="duotone" className="text-rose-500" />
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">祝日・会社休業日</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger data-testid="hol-year-select" className="h-10 w-24 font-semibold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}年</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} data-testid="hol-reload"
            className="h-10"><ArrowsClockwise size={14} className="mr-1" />再読込</Button>
        </div>
      </div>

      {/* Weekend settings card */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4" data-testid="hol-weekend-card">
        <h2 className="text-sm font-semibold text-zinc-800 mb-3 flex items-center gap-1.5">
          <Info size={14} weight="duotone" className="text-sky-500" /> 曜日設定（グローバル）
        </h2>
        <div className="flex items-center gap-6 flex-wrap">
          <label className="inline-flex items-center gap-x-3 gap-y-1 flex-wrap cursor-pointer" data-testid="hol-sat-off">
            <Switch checked={view.weekend?.saturday_off ?? true}
              onCheckedChange={(v) => updateWeekend('saturday_off', v)} />
            <span className="text-sm text-zinc-800 font-medium whitespace-nowrap">土曜を休みとする</span>
            <span className="text-xs text-zinc-500">（OFFにすると土曜も平日扱い）</span>
          </label>
          <label className="inline-flex items-center gap-3 cursor-pointer" data-testid="hol-sun-off">
            <Switch checked={view.weekend?.sunday_off ?? true}
              onCheckedChange={(v) => updateWeekend('sunday_off', v)} />
            <span className="text-sm text-zinc-800 font-medium">日曜を休みとする</span>
          </label>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="祝日" value={officialItems.length} icon="rose" testId="hol-stat-official" />
        <StatCard label="会社休業日" value={companyItems.length} icon="orange" testId="hol-stat-company" />
        <StatCard label="上書き（平日化）" value={overriddenCount} icon="sky" testId="hol-stat-overridden" />
        <StatCard label="非稼働日 合計" value={officialItems.length - overriddenCount + companyItems.length}
          icon="zinc" testId="hol-stat-nonwork" />
      </div>

      {/* Company holidays management */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm">
        <div className="p-4 flex items-center justify-between border-b border-zinc-200">
          <h2 className="text-sm font-semibold text-zinc-800">会社休業日（{year}年）</h2>
          <Button size="sm" onClick={openNewCompany} data-testid="hol-add-company"
            className="h-9 bg-teal-700 hover:bg-teal-800 shadow-sm">
            <Plus size={13} className="mr-1" weight="bold" />追加
          </Button>
        </div>
        {companyItems.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-400" data-testid="hol-company-empty">
            {year}年の会社休業日は登録されていません
          </div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {companyItems.map((it) => (
              <div key={it.date + it.name} className="p-3 flex items-center gap-3" data-testid={`hol-company-row-${it.date}`}>
                <span className="font-mono text-sm text-zinc-700 w-28">{it.date}</span>
                <span className="text-xs text-zinc-400 w-8">{WEEKDAY_LABEL[it.weekday ?? 0]}</span>
                <Badge variant="outline" className={KIND_STYLE.company}>会社休業日</Badge>
                <span className="text-sm text-zinc-800 font-medium flex-1 truncate">{it.name}</span>
                {it.note && <span className="text-xs text-zinc-500 truncate max-w-[220px]">{it.note}</span>}
                <Button size="sm" variant="ghost" onClick={() => openEditCompany(it)}
                  data-testid={`hol-company-edit-${it.date}`} className="h-8 px-2">
                  <PencilSimple size={14} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteCompany(it)}
                  data-testid={`hol-company-delete-${it.date}`} className="h-8 px-2 text-red-600 hover:bg-red-50">
                  <Trash size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Official holidays list w/ override toggle */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-semibold text-zinc-800">日本の祝日（{year}年）</h2>
          <Button size="sm" variant="outline" onClick={verify} disabled={busy || loading}
            data-testid="hol-verify" className="h-9">
            <ArrowsClockwise size={13} className="mr-1" weight="bold" />
            外部データと比較
          </Button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-400">読み込み中...</div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {officialItems.map((it) => (
              <div key={it.date + it.name}
                className={`p-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 ${it.overridden ? 'bg-zinc-50/60' : ''}`}
                data-testid={`hol-official-row-${it.date}`}>
                <span className="font-mono text-sm text-zinc-700 w-28">{it.date}</span>
                <span className="text-xs text-zinc-400 w-8">{WEEKDAY_LABEL[it.weekday ?? 0]}</span>
                <Badge variant="outline" className={KIND_STYLE.official}>祝日</Badge>
                <span className={`text-sm font-medium flex-1 min-w-[8rem] truncate ${it.overridden ? 'line-through text-zinc-400' : 'text-zinc-800'}`}>
                  {it.name}
                </span>
                {it.overridden && (
                  <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 whitespace-nowrap">
                    平日として扱う
                  </Badge>
                )}
                <Button size="sm" variant={it.overridden ? 'outline' : 'ghost'}
                  onClick={() => toggleOverride(it)}
                  disabled={overrideBusy.has(it.date)}
                  data-testid={`hol-official-toggle-${it.date}`} className="h-8 text-xs whitespace-nowrap">
                  {it.overridden ? '祝日に戻す' : '平日にする'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Verify report */}
      {verifyReport && (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4" data-testid="hol-verify-report">
          <h3 className="text-sm font-semibold text-zinc-800 mb-3 flex items-center gap-1.5">
            {verifyReport.in_sync
              ? <CheckCircle size={16} weight="duotone" className="text-emerald-500" />
              : <WarningCircle size={16} weight="duotone" className="text-amber-500" />}
            外部データ比較レポート
          </h3>
          <div className="text-xs text-zinc-500 mb-2">
            外部（holidays-jp.github.io）: {verifyReport.external_count}件 · 内蔵計算: {verifyReport.local_count}件
          </div>
          {verifyReport.in_sync ? (
            <p className="text-sm text-emerald-700">全て一致しています。</p>
          ) : (
            <div className="space-y-2">
              {verifyReport.only_external.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-700">外部にのみ存在: {verifyReport.only_external.length}件</p>
                  <ul className="text-xs text-zinc-600 mt-1">
                    {verifyReport.only_external.map((x) => <li key={x.date}>{x.date} - {x.name}</li>)}
                  </ul>
                </div>
              )}
              {verifyReport.only_local.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-sky-700">内蔵計算にのみ存在: {verifyReport.only_local.length}件</p>
                  <ul className="text-xs text-zinc-600 mt-1">
                    {verifyReport.only_local.map((x) => <li key={x.date}>{x.date} - {x.name}</li>)}
                  </ul>
                </div>
              )}
              {verifyReport.name_mismatch.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-zinc-600">名称のみ違い（軽微）: {verifyReport.name_mismatch.length}件</p>
                  <ul className="text-xs text-zinc-500 mt-1">
                    {verifyReport.name_mismatch.map((x) => (
                      <li key={x.date}>{x.date} - 内蔵「{x.local}」 vs 外部「{x.external}」</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add/edit company holiday dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="hol-company-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? '会社休業日を編集' : '会社休業日を追加'}</DialogTitle>
            <DialogDescription>
              夏季休業・年末年始・創立記念日など、会社独自の休業日を登録します。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">日付<span className="text-red-500 ml-0.5">*</span></Label>
              <Input type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="hol-company-date"
                className="h-10 mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">名称<span className="text-red-500 ml-0.5">*</span></Label>
              <Input value={form.name} placeholder="夏季休業 / 年末年始 / 創立記念日"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="hol-company-name"
                className="h-10 mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">備考</Label>
              <Input value={form.note} placeholder="任意"
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="hol-company-note"
                className="h-10 mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button onClick={saveCompany} disabled={busy}
              data-testid="hol-company-save"
              className="bg-teal-700 hover:bg-teal-800">
              {busy ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon, testId }) {
  const bg = {
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    zinc: 'bg-zinc-50 text-zinc-700 border-zinc-200',
  }[icon] || 'bg-white text-zinc-700 border-zinc-200';
  return (
    <div className={`rounded-xl border p-3 ${bg}`} data-testid={testId}>
      <div className="text-[11px] font-semibold opacity-80">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}
